import { useEffect, useId, useRef, useState, type JSX } from 'react';

import { cn } from '../lib/cn.js';
import { IconReplyThread } from '../icons/custom/icon-reply-thread.js';
import { LIBELLES_DE_REACTION, REACTIONS_DU_MESSAGE, reactionsComptees, type CleDeReaction, type ReactionsDuMessage } from './reactions.js';

export interface BarreDActionsDuMessageProps {
  /** Qui a reagi, par cle (`message.reactions`). */
  reactions: ReactionsDuMessage | undefined;
  /** Le personId de la personne connectee : ses reactions sont « pressees ». */
  moi: string | null;
  /** Absent : pas de reactions (lecture seule, compte sans fiche). */
  onReagir?: ((cle: CleDeReaction, dejaPosee: boolean) => void) | undefined;
  /** Absent : pas de bouton de fil (une reponse, ou une vue sans fil). */
  onRepondre?: (() => void) | undefined;
  /** « Repondre dans le fil » ou, en lecture seule, « Voir le fil ». */
  libelleRepondre?: string | undefined;
  /** La variante compacte du centre. */
  compacte?: boolean | undefined;
}

/**
 * Un cercle au bord de la bulle ouvre les reactions et la reponse dans le fil.
 * Les actions ne se montrent qu'a la demande, sans deplacer les messages.
 */
export function BarreDActionsDuMessage({ reactions, moi, onReagir, onRepondre, libelleRepondre = 'Répondre dans le fil', compacte }: BarreDActionsDuMessageProps): JSX.Element | null {
  const [ouvert, setOuvert] = useState(false);
  const [dessous, setDessous] = useState(false);
  const barre = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const choix = useRef<HTMLDivElement>(null);
  const choixId = useId();
  const libelleActions = onRepondre !== undefined ? 'Réagir ou répondre' : 'Ajouter une réaction';
  useEffect(() => {
    if (!ouvert) return;
    choix.current?.querySelector('button')?.focus({ preventScroll: true });
    const dehors = (event: PointerEvent) => { if (!barre.current?.contains(event.target as Node)) setOuvert(false); };
    document.addEventListener('pointerdown', dehors);
    return () => document.removeEventListener('pointerdown', dehors);
  }, [ouvert]);
  const fermer = (auClavier = true) => {
    setOuvert(false);
    if (auClavier) bouton.current?.focus({ preventScroll: true });
  };
  if (onReagir === undefined && onRepondre === undefined) return null;
  return <div ref={barre} className={cn('taki-barre-message', compacte && 'taki-barre-message-compacte')} role="group" aria-label="Actions du message" data-barre-message data-ouvert={ouvert || undefined}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOuvert(false); }}
    onKeyDown={event => { if (event.key === 'Escape' && ouvert) { event.preventDefault(); event.stopPropagation(); fermer(); } }}>
    {onReagir !== undefined && <button ref={bouton} type="button" aria-label={libelleActions} title={libelleActions}
      aria-expanded={ouvert} aria-controls={ouvert ? choixId : undefined} aria-haspopup="dialog"
      onClick={() => {
        const limite = barre.current?.closest('[data-fil-de-conversation]')?.getBoundingClientRect().top ?? 0;
        setDessous((barre.current?.getBoundingClientRect().top ?? 0) - limite < 60);
        setOuvert(value => !value);
      }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" /><path d="M8.5 14.5c1.5 2 5.5 2 7 0" /><path d="M8.5 9h.01M15.5 9h.01" strokeWidth="2.8" />
      </svg>
    </button>}
    {onReagir === undefined && onRepondre !== undefined && <button type="button" onClick={onRepondre} aria-label={libelleRepondre} title={libelleRepondre}><IconReplyThread size={18} /></button>}
    {ouvert && onReagir !== undefined && <div ref={choix} id={choixId} role="dialog" aria-label="Réactions et réponse" className="taki-choix-reaction" data-dessous={dessous || undefined}>
      {REACTIONS_DU_MESSAGE.map(({ cle, emoji }) => {
        const posee = moi !== null && (reactions?.[cle] ?? []).includes(moi);
        return <button key={cle} type="button" onClick={event => { onReagir(cle, posee); fermer(event.detail === 0); }} aria-pressed={posee}
          aria-label={libelleDuGeste(cle, posee)} title={libelleDuGeste(cle, posee)}><span aria-hidden="true">{emoji}</span></button>;
      })}
      {onRepondre !== undefined && <>
        <span className="taki-choix-reaction-separateur" aria-hidden="true" />
        <button type="button" onClick={event => { fermer(event.detail === 0); onRepondre(); }} aria-label={libelleRepondre} title={libelleRepondre}><IconReplyThread size={18} /></button>
      </>}
    </div>}
  </div>;
}

/** Le libelle du geste sur une reaction : « J’approuve », ou « Retirer : J’approuve » si elle est deja posee. Exporte pour le test. */
export function libelleDuGeste(cle: CleDeReaction, posee: boolean): string {
  return posee ? `Retirer : ${LIBELLES_DE_REACTION[cle]}` : LIBELLES_DE_REACTION[cle];
}

export interface PastillesDeReactionsProps {
  reactions: ReactionsDuMessage | undefined;
  moi: string | null;
  /** Absent : les pastilles se lisent, ne se cliquent pas. */
  onReagir?: ((cle: CleDeReaction, dejaPosee: boolean) => void) | undefined;
  className?: string | undefined;
}

/**
 * LES COMPTEURS DE REACTIONS sous le message : un emoji et son nombre, presse
 * quand la personne connectee en fait partie ; un clic pose ou retire la
 * sienne. Rien quand personne n'a reagi (aucun espace vide).
 */
export function PastillesDeReactions({ reactions, moi, onReagir, className }: PastillesDeReactionsProps): JSX.Element | null {
  const posees = reactionsComptees(reactions, moi);
  if (posees.length === 0) return null;
  return <div className={cn('mt-2 flex flex-wrap gap-1', className)} data-reactions>
    {posees.map(({ cle, emoji, personnes, deMoi }) => <button key={cle} type="button" disabled={onReagir === undefined}
      onClick={() => onReagir?.(cle, deMoi)} aria-pressed={deMoi}
      aria-label={`${LIBELLES_DE_REACTION[cle]}, ${personnes.length} réaction${personnes.length > 1 ? 's' : ''}`} title={LIBELLES_DE_REACTION[cle]}
      className={cn('flex h-6 items-center gap-1.5 rounded-pill px-2 text-xs font-medium tabular-nums outline-none focus-visible:shadow-ring disabled:cursor-default',
        deMoi ? 'bg-accent-tint-border text-ink-900 enabled:hover:bg-accent-tint' : 'bg-surface-3 text-ink-600 enabled:hover:bg-accent-tint')}>
      <span aria-hidden="true">{emoji}</span><span>{personnes.length}</span>
    </button>)}
  </div>;
}
